import type { UseQueryOptions } from "@tanstack/react-query";
import type { RiskAllocation } from "../../../api/src/securities/securities.service";
import { processJsonResponse, useAuthenticatedFetch } from "../lib/api";

/**
 * The Client handlers send every percent on 0 to 100
 * (`an-decision-01a0ef4b7e9377c9` row 1; `api/src/lib/client-percents.ts`).
 * The deserializers here divide each by 100 where the console reads the
 * response, so every screen keeps computing and displaying from the 0 to 1
 * ratio it always read. Every read of one route converts, because the
 * react-query cache holds one copy per key.
 */

/**
 * The decimal places of a Client percent on 0 to 1: the API sends 6 places on
 * 0 to 100 (`CLIENT_PERCENT_DECIMALS`, `api/src/lib/client-percents.ts`), so 8
 * on 0 to 1.
 */
const CLIENT_RATIO_DECIMALS = 8;

/**
 * `percent` (0 to 100) on 0 to 1, rounded to {@link CLIENT_RATIO_DECIMALS}, so
 * `25` comes back as `0.25` and not as a neighbouring double. `null` (a
 * non-finite ratio the API sent as `null`) stays `null`.
 */
export function percentToRatio(percent: number): number {
  if (percent === null) {
    return percent;
  }
  return (
    Math.round(percent * 10 ** (CLIENT_RATIO_DECIMALS - 2)) /
    10 ** CLIENT_RATIO_DECIMALS
  );
}

function recordRatios(
  percents: Record<string, number>,
): Record<string, number> {
  return Object.fromEntries(
    Object.entries(percents).map(([key, percent]) => [
      key,
      percentToRatio(percent),
    ]),
  );
}

/** Each risk level's share on 0 to 1 in place of 0 to 100. */
export function deserializeRiskAllocationPercents(
  allocation: RiskAllocation,
): RiskAllocation {
  return recordRatios(allocation);
}

type HoldingWithRiskAllocation = {
  security: { riskAllocation: RiskAllocation } | null;
};

/** `holding` with `security.riskAllocation` on 0 to 1. */
export function deserializeHoldingPercents<
  THolding extends HoldingWithRiskAllocation,
>(holding: THolding): THolding {
  return holding.security == null
    ? holding
    : {
        ...holding,
        security: {
          ...holding.security,
          riskAllocation: deserializeRiskAllocationPercents(
            holding.security.riskAllocation,
          ),
        },
      };
}

/**
 * `{ current, target }` on 0 to 1. An absent side stays absent; the API sends
 * both (`current: {}` for an account with no position on
 * `GET /households/:id/risk-allocation`).
 */
export function deserializeCurrentAndTargetPercents<
  TAllocation extends { current: RiskAllocation; target: RiskAllocation },
>(allocation: TAllocation): TAllocation {
  return {
    ...allocation,
    current:
      allocation.current == null
        ? allocation.current
        : deserializeRiskAllocationPercents(allocation.current),
    target:
      allocation.target == null
        ? allocation.target
        : deserializeRiskAllocationPercents(allocation.target),
  };
}

/** `performance` on 0 to 1. */
export function deserializePerformancePercents<
  TPerformance extends { performance: number },
>(performance: TPerformance): TPerformance {
  return {
    ...performance,
    performance: percentToRatio(performance.performance),
  };
}

type AccountPercentKeys = {
  holdings?: HoldingWithRiskAllocation[];
  riskAllocation?: { current: RiskAllocation; target: RiskAllocation };
  cashBalanceWeight?: number;
  performanceYTD?: { performance: number };
};

/**
 * `account` with each percent key it carries on 0 to 1:
 * `holdings[].security.riskAllocation`, `riskAllocation.current` and
 * `.target`, `cashBalanceWeight` and `performanceYTD.performance`
 * (`accountPercents`, `api/src/lib/client-percents.ts`). A key the account
 * does not carry stays absent.
 */
export function deserializeAccountPercents<TAccount extends object>(
  sent: TAccount,
): TAccount {
  // `Account` carries none of the keys, so the parameter is any object.
  const account = sent as AccountPercentKeys;
  return {
    ...sent,
    ...(account.holdings == null
      ? {}
      : { holdings: account.holdings.map(deserializeHoldingPercents) }),
    ...(account.riskAllocation == null
      ? {}
      : {
          riskAllocation: deserializeCurrentAndTargetPercents(
            account.riskAllocation,
          ),
        }),
    ...(account.cashBalanceWeight == null
      ? {}
      : { cashBalanceWeight: percentToRatio(account.cashBalanceWeight) }),
    ...(account.performanceYTD == null
      ? {}
      : {
          performanceYTD: deserializePerformancePercents(
            account.performanceYTD,
          ),
        }),
  };
}

/**
 * `GET /households/:id` with `accounts=true`: each account's percents on 0 to
 * 1. Without `accounts` the household carries no percent and is returned as
 * sent.
 */
export function deserializeHouseholdAccountPercents<THousehold extends object>(
  household: THousehold,
): THousehold {
  const { accounts } = household as { accounts?: object[] };
  return accounts == null
    ? household
    : { ...household, accounts: accounts.map(deserializeAccountPercents) };
}

/** Each historical point's `performance` on 0 to 1. */
export function deserializeHistoricalPerformancePercents<
  TPoint extends { performance: number },
>(points: TPoint[]): TPoint[] {
  return points.map(deserializePerformancePercents);
}

/** Each `allocation` on 0 to 1; `null` stays `null`. */
export function deserializeAllocationDetailPercents<
  TDetail extends { allocation: number },
>(details: TDetail[] | null): TDetail[] | null {
  return details === null
    ? null
    : details.map((detail) => ({
        ...detail,
        allocation: percentToRatio(detail.allocation),
      }));
}

/**
 * The two `allocation-details` reads: `current`, `target` and (households
 * only) `householdModelTarget`, each `allocation` on 0 to 1. The constraint
 * is a mapped type, not a `Record`, so a class type such as
 * `HouseholdAllocationDetails` (`api/src/households/households.service.ts`),
 * which has no index signature, satisfies it.
 */
export function deserializeAllocationDetailsPercents<
  TDetails extends { [K in keyof TDetails]: { allocation: number }[] | null },
>(details: TDetails): TDetails {
  return Object.fromEntries(
    Object.entries(details).map(([key, value]) => [
      key,
      deserializeAllocationDetailPercents(
        value as { allocation: number }[] | null,
      ),
    ]),
  ) as TDetails;
}

/** Each weight of `model` on 0 to 1; `null` stays `null`. */
export function deserializeModelSummaryPercents<
  TSummary extends { model: Record<string, number> },
>(summary: TSummary): TSummary;
export function deserializeModelSummaryPercents<
  TSummary extends { model: Record<string, number> },
>(summary: TSummary | null): TSummary | null;
export function deserializeModelSummaryPercents<
  TSummary extends { model: Record<string, number> },
>(summary: TSummary | null): TSummary | null {
  return summary === null
    ? null
    : { ...summary, model: recordRatios(summary.model) };
}

/**
 * `growthAssumption`, `defaultWithholdingRateFed` and
 * `defaultWithholdingRateState` on 0 to 1, where the RMD screens read and
 * edit them. A `null` rate stays `null`.
 */
export function deserializeRmdProfilePercents<
  TProfile extends {
    growthAssumption: number;
    defaultWithholdingRateFed: number | null;
    defaultWithholdingRateState: number | null;
  },
>(profile: TProfile): TProfile {
  return {
    ...profile,
    growthAssumption: percentToRatio(profile.growthAssumption),
    defaultWithholdingRateFed:
      profile.defaultWithholdingRateFed === null
        ? null
        : percentToRatio(profile.defaultWithholdingRateFed),
    defaultWithholdingRateState:
      profile.defaultWithholdingRateState === null
        ? null
        : percentToRatio(profile.defaultWithholdingRateState),
  };
}

/**
 * A `responseCallback` for `useAuthenticatedFetch` that reads the JSON body
 * and passes its `data` through `deserialize`, so the cached response is
 * already on 0 to 1. A body with no `data` is returned as read.
 */
export function withDataPercents<TData>(
  deserialize: (data: TData) => TData,
): <TBody>(res: Response) => Promise<TBody> {
  return async <TBody>(res: Response) => {
    const body = (await processJsonResponse(res)) as { data?: TData };
    return (
      body?.data == null ? body : { ...body, data: deserialize(body.data) }
    ) as TBody;
  };
}

/**
 * `useAuthenticatedFetch(url)` whose response `data` passes through
 * `deserialize` before it is cached, keyed by `[url]` as that hook keys it.
 * Every reader of a Client route that sends a percent reads it through this,
 * so the one cached copy is on 0 to 1 whichever screen fetched it first.
 */
export function useAuthenticatedPercentFetch<TBody extends { data: unknown }>(
  url: string,
  deserialize: (data: TBody["data"]) => TBody["data"],
  fetchOptions: Parameters<typeof useAuthenticatedFetch>[1] = {},
  queryOptions?: Omit<UseQueryOptions<TBody>, "queryKey" | "queryFn">,
) {
  return useAuthenticatedFetch<TBody>(
    url,
    fetchOptions,
    queryOptions,
    [url],
    withDataPercents(deserialize),
  );
}
